import React, { FC, useMemo } from 'react'

import { Chart, Settings, LIGHT_THEME, Partition, PartitionLayout, ShapeTreeNode } from '@elastic/charts'
import { EuiPanel, EuiTitle, EuiText, EuiSpacer, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { AIPlan } from 'api/interfaces/AIPlan'
import { InsertSpaces } from 'utils/EnumToFriendly'
import PlannerTvLegend from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/PlannerTvLegend'

export interface AIPlanConnectedTvNetworksProps {
  aiPlan: AIPlan
  connectedTvRef?: React.RefObject<HTMLDivElement>
}

const adPlacementCategoryLabels: Record<string, string> = {
  Major: 'Major Networks',
  Sports: 'Sports Networks',
  Entertainment: 'Entertainment Networks',
  Family: 'Family Friendly Networks',
  News: 'News Networks',
  Lifestyle: 'Lifestyle Networks'
}

const getCategoryColor = (category: string): string => {
  const colorMap: Record<string, string> = {
    Major: '#f49342',
    Sports: '#2B70F7',
    Entertainment: '#9170B8',
    Family: '#54B399',
    News: '#D36086',
    Lifestyle: '#AA6556'
  }
  return colorMap[category] || '#E7664C'
}

const transformTvNetworkData = (tvNetworkDistribution?: Record<string, number>) => {
  if (!tvNetworkDistribution || Object.keys(tvNetworkDistribution).length === 0) {
    return []
  }

  return Object.entries(tvNetworkDistribution).map(([category, percentage]) => ({
    key: category,
    label: adPlacementCategoryLabels[category] || InsertSpaces(category),
    percent: percentage
  }))
}

const AIPlanConnectedTvNetworks: FC<AIPlanConnectedTvNetworksProps> = ({ aiPlan, connectedTvRef }) => {
  const categories = useMemo(() => transformTvNetworkData(aiPlan?.tvNetworkDistribution), [aiPlan?.tvNetworkDistribution])

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {}
    categories.forEach(category => {
      map[category.key] = getCategoryColor(category.key)
    })
    return map
  }, [categories])

  return (
    <EuiPanel paddingSize='m'>
      <EuiFlexGroup alignItems='center' responsive={false}>
        <EuiFlexItem />
        <EuiFlexItem grow={false}>
          <EuiTitle size='s'>
            <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>CTV: Placement Categories</h3>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem />
      </EuiFlexGroup>
      <EuiText size='s' style={{ whiteSpace: 'pre-wrap' }}>
        Based on chosen audience and historic delivery data, CTV impressions are projected to appear across premium outlets in the following categories. Precise distribution may vary based on live auction constraints.
      </EuiText>

      <EuiSpacer size='xl' />

      <div ref={connectedTvRef}>
        <div style={{ marginTop: -30, marginBottom: -20 }}>
          <Chart size={{ height: 260 }}>
            <Settings
              baseTheme={LIGHT_THEME}
              showLegend={false}
              theme={{
                partition: {
                  linkLabel: {
                    maximumSection: Infinity,
                    textColor: 'rgba(0, 0, 0, 1)'
                  },
                  emptySizeRatio: 0.6
                }
              }}
            />
            <Partition
              data={categories as any[]}
              id='AIPlanTvBreakdown'
              valueAccessor={(d: any) => d.percent}
              valueGetter='percent'
              valueFormatter={() => ''}
              percentFormatter={(d: number) => `${Math.round((d + Number.EPSILON) * 100) / 100}%`}
              layout={PartitionLayout.sunburst}
              layers={[
                {
                  groupByRollup: (d: any) => d.key,
                  nodeLabel: (d: any) => InsertSpaces(d),
                  fillLabel: {
                    fontFamily: "'Inter', BlinkMacSystemFont, Helvetica, Arial, sans-serif",
                    fontWeight: 400,
                    maxFontSize: 24
                  },
                  shape: {
                    fillColor: (node: ShapeTreeNode) => colorMap[String(node.dataName)]
                  }
                }
              ]}
            />
          </Chart>
        </div>

        <EuiSpacer size='xl' />

        <PlannerTvLegend colorMap={colorMap} />
      </div>
    </EuiPanel>
  )
}

export default AIPlanConnectedTvNetworks
